import { Offcanvas, Form, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";

interface AddSubscriptionProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
  activeTab: "ACTIVE" | "PENDING" | "EXPIRED" | "DELETED";
}

type FormData = {
  organizationId: string;
  userId: string;
  planId: string;
  paymentstatus: "PAID" | "PENDING";
  startDate: string;
  endDate: string;
  notes?: string;
  paymentGatewayId?: string;
  status?: "ACTIVE" | "PENDING" | "EXPIRED";
};

const AddSubscription = ({ show, handleClose, data, activeTab }: AddSubscriptionProps) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [orgList, setOrgList] = useState<any[]>([]);
  // const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [userList, setUserList] = useState<any[]>([]);
  const [planList, setPlanList] = useState<any[]>([]);

  const isEdit = Boolean(data);
  const isPendingTab = activeTab === "ACTIVE" && isEdit;
  const selectedOrg = watch("organizationId");

  const loadOrganizations = () => {
    WebService.getAPI({ action: "organizations/list-all" })
      .then((res: any) => setOrgList(res.data || []))
      .catch(() => setOrgList([]));
  };

  const loadUsers = (orgId: string) => {
    if (!orgId) {
      setUserList([]); // 🔥 no org selected -> no users
      return;
    }

    WebService.getAPI({ action: `users/list-all-by-org/${orgId}` })
      .then((res: any) => setUserList(res.data || []))
      .catch(() => setUserList([]));
    // } else {
    //   WebService.getAPI({ action: "users/list-all" })
    //     .then((res: any) => setUserList(res.data || []))
    //     .catch(() => setUserList([]));
    // }
  };

  const loadPlans = () => {
    WebService.getAPI({ action: "plan/list-all" })
      .then((res: any) => setPlanList(res.data || []))
      .catch(() => setPlanList([]));
  };

  useEffect(() => {
    if (show && data && planList.length > 0) {
      setValue("planId", data.planId); // 👈 FORCE SET VALUE
    }
  }, [show, data, planList]);

  useEffect(() => {
    if (show) {
      loadOrganizations();
      // loadUsers();
      loadPlans();

      if (isEdit) {
        const orgId = data?.organization?._id || "";
        // setSelectedOrg(orgId || "");

        reset({
          organizationId: orgId,
          userId: data?.user?._id,
          planId: data?.plan?._id || "",
          paymentstatus: data?.transaction?.status || "PAID",
          startDate: data?.startDate?.split("T")[0] || "",
          endDate: data?.endDate?.split("T")[0] || "",
          notes: data?.notes || "",
          paymentGatewayId: data?.transaction?.paymentGatewayId || "",
          status: data?.status || "ACTIVE",
        });
        loadUsers(orgId)
      } else {
        // setSelectedOrg("");
        reset({
          organizationId: "",
          userId: "",
          planId: "",
          paymentstatus: "PAID",
          startDate: "",
          endDate: "",
          notes: "",
          paymentGatewayId: "",
          status: "ACTIVE",
        });
        setUserList([]);
      }
    }
  }, [show, data]);

  // 🔥 Load users only after org selected
  useEffect(() => {
    if (selectedOrg) {
      loadUsers(selectedOrg);
      if (!isEdit) {
        setValue("userId", "");
      } // 🔥 reset user when org changes
    } else {
      setUserList([]);
      setValue("userId", "");
    }
  }, [selectedOrg]);

  // useEffect(() => {
  //   if (isEdit && userList.length > 0) {
  //     reset({
  //       userId: data?.user?._id || "",
  //       planId: data?.plan?._id || "",
  //       paymentstatus: data?.transaction?.status || "PAID",
  //       startDate: data?.startDate?.split("T")[0] || "",
  //       endDate: data?.endDate?.split("T")[0] || "",
  //       notes: data?.notes || "",
  //       paymentGatewayId: data?.transaction?.paymentGatewayId || "",
  //       status: data?.status || "ACTIVE",
  //     });
  //   }
  // }, [userList]);

  const onSubmit = (formData: FormData) => {
    setLoading(true);
    let payload: any = {};

    if (isEdit) {
      if (!isPendingTab) {
        payload = { userId: formData.userId, paymentStatus: formData.paymentstatus, status: formData.status, startDate: formData.startDate, endDate: formData.endDate, notes: formData.notes, paymentGatewayId: formData.paymentGatewayId };
      } else {
        payload = { userId: formData.userId, paymentStatus: formData.paymentstatus, status: formData.status, startDate: formData.startDate, endDate: formData.endDate, notes: formData.notes, paymentGatewayId: formData.paymentGatewayId };
      }
    } else {
      payload = { ...formData };
    }

    const apiCall = isEdit
      ? WebService.putAPI({
        action: `userSubscriptions/update-user/${data._id}`,
        body: payload,
      })
      : WebService.postAPI({
        action: "userSubscriptions/create",
        body: payload,
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message || "Saved successfully!");
        handleClose();
        reset();
      })
      .catch((e) => toast.error(e?.response?.data?.message || "Error"))
      .finally(() => setLoading(false));
  };

  const startDate = watch("startDate");

  return (
    <Offcanvas
      show={show}
      placement="end"
      onHide={() => {
        handleClose();
        reset();
      }}
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{isEdit ? "Edit Subscription" : "Add Subscription"}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          <Card className="p-3 mt-3">
            <h6 className="fw-bold mb-3">Subscription Details</h6>

            {/* ORGANIZATION */}
            <Form.Group className="mb-3">
              <Form.Label>Organization <span className="text-danger">*</span></Form.Label>
              <Form.Select
                {...register("organizationId", {
                  required: "Organization is required",
                })}
                disabled={isEdit}
                value={watch("organizationId") || ""}
                onChange={(e) => setValue("organizationId", e.target.value, { shouldValidate: true })}
              >
                <option value="">Select  Organizations</option>
                {orgList.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
              {errors.organizationId && (
                <p className="text-danger">{errors.organizationId.message}</p>
              )}
            </Form.Group>

            {/* USER */}
            <Form.Group className="mb-3">
              <Form.Label>User <span className="text-danger">*</span></Form.Label>
              <Form.Select {...register("userId", { required: "User is required" })}
                value={watch("userId") || ""}   // ✅ add this
                onChange={(e) => setValue("userId", e.target.value, { shouldValidate: true })}>
                <option value="">Select User</option>
                {userList.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.fullName}({u.email})
                  </option>
                ))}
              </Form.Select>
              {errors.userId && <p className="text-danger">{errors.userId.message}</p>}
            </Form.Group>

            {/* PLAN */}
            <Form.Group className="mb-3">
              <Form.Label>Plan <span className="text-danger">*</span></Form.Label>
              <Form.Select
                disabled={isEdit}
                {...register("planId", { required: "Plan is required" })}
              >
                <option value="">Select Plan</option>
                {planList.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.planName}
                  </option>
                ))}
              </Form.Select>
              {errors.planId && <p className="text-danger">{errors.planId.message}</p>}
            </Form.Group>

            {/* PAYMENT GATEWAY ID */}
            <Form.Group className="mb-3">
              <Form.Label>Payment ID</Form.Label>
              <Form.Control
                type="text"
                // disabled={isEdit}
                placeholder="Enter payment gateway ID"
                {...register("paymentGatewayId", {
                  required: false,
                })}
              />
            </Form.Group>

            {/* STATUS */}
            <Form.Group className="mb-3">
              <Form.Label>Payment Status <span className="text-danger">*</span></Form.Label>
              <Form.Select
                // disabled={isPendingTab}
                {...register("paymentstatus", { required: "Status is required" })}
              >
                <option value="PAID">PAID</option>
                <option value="PENDING">PENDING</option>
              </Form.Select>
              {errors.paymentstatus && (
                <p className="text-danger">{errors.paymentstatus.message}</p>
              )}
            </Form.Group>
          </Card>

          {/* DATES */}
          <Card className="p-3 mt-3">
            <h6 className="fw-bold mb-3">Dates</h6>

            <Form.Group className="mb-3">
              <Form.Label>Subscription Start Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                // min={new Date().toISOString().split("T")[0]}
                // disabled={isEdit}
                {...register("startDate", { required: "Subscription date is required" })}
              />
              {errors.startDate && (
                <p className="text-danger">{errors.startDate.message}</p>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiration Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                // min={startDate || undefined}
                // disabled={isEdit}
                {...register("endDate", {
                  required: "Expiration date is required", validate: (value) =>
                    !startDate || value >= startDate || "Expiration date must be after subscription date",
                })}
              />
              {errors.endDate && (
                <p className="text-danger">{errors.endDate.message}</p>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status <span className="text-danger">*</span></Form.Label>
              <Form.Select
                // disabled={!isPendingTab}
                {...register("status", { required: "Status is required" })}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="PENDING">PENDING</option>
                <option value="EXPIRED">EXPIRED</option>
              </Form.Select>
              {errors.status && (
                <p className="text-danger">{errors.status.message}</p>
              )}
            </Form.Group>
          </Card>

          {/* NOTES */}
          <Card className="p-3 mt-3">
            <h6 className="fw-bold mb-2">Notes</h6>
            <Form.Control as="textarea" rows={3} {...register("notes")} />
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          isLoading={isLoading}
          label={isEdit ? "Update" : "Save"}
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddSubscription;
